import { type PropsWithChildren, useCallback, useMemo, useState } from "react";
import type {
  Airport,
  Person,
  Prices,
  SeatSlice,
} from "@b2bportal/air-booking-api";
import type { SeatSegment } from "@b2bportal/air-booking-api/lib/api";
import type { GordianSeat } from "@b2bportal/air-seats-api";
import SeatsFrame from "@checkout/components/Seats/SeatsFrame";
import type { HopperSeatTotal } from "@checkout/states/Products/Flight/states/SeatSelection/context";
import { trackEvent } from "@hopper-b2b/api";
import {
  type SeatEvents,
  SeatEventTypes,
  useCheckoutSend,
} from "@hopper-b2b/checkout";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";

import {
  SKIPPED_SEAT_SELECTION,
  type GordianSeatSegment,
  type SeatInfo,
  type TripDetails,
} from "@hopper-b2b/types";
import {
  ActionLink,
  Divider,
  IconComponent,
  IconName,
  MobilePopoverCard,
} from "@hopper-b2b/ui";

import clsx from "clsx";
import { GenericHeader } from "../../../../../../components/GenericHeader";
import { useDarkModePreferred } from "../../../../../../utils/colors";
import {
  seatMapOptions,
  styleString,
  hopperStyleString,
  hopperSeatMapOptions,
} from "../../iframeStyles";
import seatMapDarkModeStyles from "../../seatMapDarkMode.scss?inline";
import { FlightSliceRow } from "../FlightSliceRow";
import styles from "./styles.module.scss";

export interface SelectedSeatSegment {
  seats: SeatInfo[];
  departureDate: string;
  destinationCode: string;
  originCode: string;
}

interface ISeatsProps extends PropsWithChildren {
  // Connected Seats
  airports: { [key: string]: Airport } | undefined;
  cheapestSeat: Prices | undefined;
  tripDetails: TripDetails;
  seatSlices: SeatSlice[] | undefined;
  selectedSeats?: GordianSeat[];
  passengers?: Person[];
  totalSeatPricing?: number;
  seatMapHtml: string | undefined;
  selectedSeatSegments?: SelectedSeatSegment[];
  // Other
  fonts: string[];
  fontFamilies: string[];
  onNext?: () => void;
  onBack?: () => void;
  onSeatsSelected: (
    selectedSeats: GordianSeat[],
    selectedSeatSegments: GordianSeatSegment[],
    selectedSeatTotal?: HopperSeatTotal
  ) => void;
}

export const SelectedSeats = ({
  airports,
  cheapestSeat,
  tripDetails,
  seatSlices,
  selectedSeats,
  passengers,
  totalSeatPricing,
  seatMapHtml,
  selectedSeatSegments,
  fonts,
  fontFamilies,
  onNext,
  onBack,
  onSeatsSelected,
}: ISeatsProps) => {
  const { t, formatFiatCurrency, formatCurrency } = useI18nContext();
  const [showSeatMap, setShowSeatMap] = useState(false);
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);

  const [isEditMode, setIsEditMode] = useState(false);
  const [seatEditIndex, setSeatEditIndex] = useState(undefined);
  const send = useCheckoutSend<SeatEvents>();

  const darkModeEnabled = useDarkModePreferred();

  const paxWithSeatPricing = useMemo(() => {
    return passengers.map((passenger) => {
      // get the passenger's seats
      const passengerPrice = selectedSeats
        .filter((seat) => seat.person_id === passenger.id)
        .map((seat) => {
          return parseFloat(seat.price);
        })
        // combine the prices
        .reduce((prevPrice, price) => prevPrice + price, 0);
      return {
        passengerName: `${passenger.givenName} ${passenger.surname}`,
        total: passengerPrice,
      };
    });
  }, [passengers, selectedSeats]);

  const closeSeatMap = useCallback(() => {
    setShowSeatMap(false);
  }, []);

  const onEdit = useCallback(
    (seatSegment: SeatSegment) => {
      const fmap = seatSlices.flatMap((el) => el.segments);
      setSeatEditIndex(fmap.indexOf(seatSegment));
      setShowSeatMap(true);
      setIsEditMode(true);
    },
    [seatSlices]
  );

  const onSkip = useCallback(() => {
    trackEvent({
      eventName: SKIPPED_SEAT_SELECTION,
      properties: {},
    });
    send({ type: SeatEventTypes.SKIP_SEAT_SELECTION });
    onNext();
  }, [onNext, send]);

  const openPriceBreakdownHandler = useCallback(() => {
    setShowPriceBreakdown(true);
  }, []);

  const closePriceBreakdownHandler = useCallback(() => {
    setShowPriceBreakdown(false);
  }, []);

  return (
    <>
      <div className={styles.SelectedSeats}>
        {!showSeatMap ? (
          <div className="overview">
            <GenericHeader title={t("seatSelection")} onBack={onBack} />

            <div className="subheader">
              <h3>{t("seats.selectYourSeats")}</h3>
              <p className="subtext">
                <Trans
                  i18nKey={"seats.cheapestSeatPerTraveler"}
                  values={{
                    price: formatFiatCurrency({ ...cheapestSeat.fiat }),
                  }}
                  components={[<strong />]}
                />
              </p>
            </div>

            <div className="content">
              <Divider className={styles.Break} />
              {seatSlices && seatSlices.length > 0 ? (
                <>
                  <p className="slice-header">{t("seats.outboundFlights")}</p>
                  {seatSlices[0] && (
                    <FlightSliceRow
                      slice={tripDetails.slices[0]}
                      seatSlice={seatSlices[0]}
                      airports={airports}
                      selectedSeatSegments={selectedSeatSegments}
                      passengers={passengers}
                      onEdit={onEdit}
                    />
                  )}
                  {/* If round trip (2 flight slices), show return flight.
                      API can structure seat data in 2 different ways:
                      - CASE 1: seatSlices[0] for outbound, seatSlices[1] for return (2 separate arrays)
                      - CASE 2: seatSlices[0] contains ALL segments for both flights (1 combined array) */}
                  {tripDetails.slices.length > 1 && (
                    <>
                      <Divider className={styles.Break} />
                      <p className="slice-header">{t("seats.returnFlights")}</p>
                      {seatSlices.length > 1 && seatSlices[1] ? (
                        // Case 1: Separate seat slice for return flight
                        <FlightSliceRow
                          slice={tripDetails.slices[1]}
                          seatSlice={seatSlices[1]}
                          airports={airports}
                          selectedSeatSegments={selectedSeatSegments}
                          passengers={passengers}
                          onEdit={onEdit}
                        />
                      ) : seatSlices[0] &&
                        seatSlices[0].segments?.length >
                          tripDetails.slices[0].segmentDetails.length ? (
                        // Case 2: Single seat slice contains all segments - show return flight with same seatSlice
                        <FlightSliceRow
                          slice={tripDetails.slices[1]}
                          seatSlice={seatSlices[0]}
                          airports={airports}
                          selectedSeatSegments={selectedSeatSegments}
                          passengers={passengers}
                          onEdit={onEdit}
                        />
                      ) : null}
                    </>
                  )}
                </>
              ) : null}
            </div>
            <div className="cta-container">
              {/* TODO Add in price component when we select flights */}
              {/* {selectedSeats.length > 0 ? "price" : null} */}

              <button
                className="cta"
                onClick={selectedSeats.length === 0 ? onSkip : onNext}
              >
                {selectedSeats.length === 0
                  ? t("seats.cancelSeatSelection")
                  : t("continue")}
              </button>
            </div>
          </div>
        ) : (
          <SeatsFrame
            seatMapHtml={seatMapHtml}
            seatMapOptions={seatMapOptions}
            styleString={styleString}
            segmentIndex={seatEditIndex}
            isEditMode={isEditMode}
            fonts={fonts}
            fontFamilies={fontFamilies}
            onSetOpen={setShowSeatMap}
            onCancel={closeSeatMap}
            onSeatsSelected={onSeatsSelected}
            hopperSeatMapOptions={hopperSeatMapOptions}
            hopperStyleString={
              darkModeEnabled
                ? hopperStyleString + seatMapDarkModeStyles
                : hopperStyleString
            }
          />
        )}
      </div>
      <MobilePopoverCard
        centered
        fullScreen
        open={showPriceBreakdown}
        onClose={closePriceBreakdownHandler}
        topRightButton={
          <ActionLink
            className="review-price-breakdown-modal-close-button"
            content={
              <IconComponent
                ariaLabel={t("close")}
                className={"close-button-icon"}
                name={IconName.Close}
              />
            }
            label={t("close")}
            onClick={closePriceBreakdownHandler}
          />
        }
        headerElement={
          <div className="review-price-breakdown-modal-header">
            {t("priceBreakdown")}
          </div>
        }
        className="review-price-breakdown-modal"
        contentClassName={clsx("review-price-breakdown-modal-content", {
          darkMode: darkModeEnabled,
        })}
      >
        {paxWithSeatPricing.map((pax, i) => (
          <div
            className={styles.PassengerSeatRow}
            key={`${pax.passengerName}-${i}`}
          >
            <div className="content">
              <p className="pax-name">{pax.passengerName}</p>
              <p className="subtitle">{t("seatSelection")}</p>
            </div>
            <p className="price">{formatCurrency(pax.total)}</p>
          </div>
        ))}
        <hr className={styles.Break} />
        <div className={styles.TotalPricing}>
          <p>{t("total")}</p>
          <p>{formatCurrency(totalSeatPricing)}</p>
        </div>
      </MobilePopoverCard>
    </>
  );
};
